import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { SectionHeader } from "@/components/common/Widgets";
import { LOAN_TYPES } from "@/lib/format";
import { Check, Building2, UserCheck, Save, Loader2 } from "lucide-react";
import useFormDraft from "@/lib/useFormDraft";
import DraftBanner from "@/components/common/DraftBanner";
import EligibilityPreview from "@/components/common/EligibilityPreview";

const STEPS = ["Customer", "Loan Type", "Facility Details", "Notes", "Review"];

export default function ApplicationNewPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const draftIdParam = searchParams.get("draft");
  const [step, setStep] = useState(0);
  const [customers, setCustomers] = useState([]);
  const [form, setForm] = useState({
    customer_id: "",
    loan_type: "CC",
    origin: "BRANCH",  // BRANCH | FIELD_OFFICE
    assigned_fo_id: "",
    purpose: "",
    cc_proposed: "",
    tl_proposed: "",
    tenor_months: 60,
    interest_rate: 11.5,
    cgtmse_requested: false,
    notes: "",
    // Personal
    personal_loan_amount: "",
    net_monthly_income: "",
    monthly_expense: "",
    existing_emi: "",
    employer_name: "",
    employer_type: "Private",
    foir_pct: "",
    // Housing
    housing_loan_amount: "",
    property_cost_lakhs: "",
    property_location: "",
    rera_number: "",
    builder_name: "",
    ltv_pct: "",
    property_type: "New",
  });
  const [busy, setBusy] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [fos, setFos] = useState([]);

  // Auto-draft + Resume (backend-synced for cross-device support)
  const customerLabel = (customers.find((c) => c.id === form.customer_id) || {}).name || "Unknown customer";
  const draftMetaPayload = {
    display_name: `${customerLabel} · ${form.loan_type}`,
    loan_type: form.loan_type,
    customer_id: form.customer_id,
  };
  const { hasDraft, draftMeta, restore, clear: clearDraft, saveNow } = useFormDraft(
    "application-new",
    form,
    { step },
    { backend: true, kind: "application", meta: draftMetaPayload }
  );
  const [showBanner, setShowBanner] = useState(false);
  useEffect(() => { setShowBanner(hasDraft && !draftIdParam); }, [hasDraft, draftIdParam]);
  const handleResume = () => {
    const r = restore();
    if (r?.form) setForm((f) => ({ ...f, ...r.form }));
    if (r?.extra?.step !== undefined) setStep(r.extra.step);
    setShowBanner(false);
    toast.success("Draft restored");
  };
  const handleDiscard = async () => {
    await clearDraft();
    setShowBanner(false);
    toast.info("Draft discarded");
  };

  // Deep-link resume: when navigated with /applications/new?draft=<id>, hydrate from that draft.
  useEffect(() => {
    if (!draftIdParam) return;
    (async () => {
      try {
        const r = await api.get(`/drafts/${draftIdParam}`);
        if (r?.data?.payload) {
          setForm((f) => ({ ...f, ...r.data.payload }));
          if (typeof r.data.step === "number") setStep(r.data.step);
          toast.success("Draft resumed from My Drafts");
        }
      } catch (_) {
        toast.error("Could not load that draft");
      }
    })();
  }, [draftIdParam]);

  useEffect(() => { (async () => {
    const res = await api.get("/customers");
    setCustomers(res.data);
    if (res.data.length && !form.customer_id) setForm((f) => ({ ...f, customer_id: res.data[0].id }));
    try {
      const u = await api.get("/auth/users");
      setFos((u.data || []).filter((x) => x.role === "FO" && x.is_active !== false));
    } catch (e) { /* not all roles can list */ }
  })(); /* eslint-disable-next-line */ }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const next = () => setStep((s) => Math.min(s + 1, STEPS.length - 1));
  const prev = () => setStep((s) => Math.max(s - 1, 0));

  const handleSaveDraftAndExit = async () => {
    setSavingDraft(true);
    try {
      const result = await saveNow();
      if (result?.id) {
        toast.success("Draft saved. You can resume from My Drafts.");
      } else {
        toast.info("Draft saved locally.");
      }
      navigate("/drafts");
    } catch (e) {
      toast.error("Could not save draft");
    } finally {
      setSavingDraft(false);
    }
  };

  const submit = async () => {
    if (!form.customer_id) return toast.error("Select a customer");
    setBusy(true);
    try {
      const payload = {
        customer_id: form.customer_id,
        loan_type: form.loan_type,
        origin: form.origin || "BRANCH",
        assigned_fo_id: form.assigned_fo_id || null,
        purpose: form.purpose || null,
        cc_proposed: form.cc_proposed ? Number(form.cc_proposed) : null,
        tl_proposed: form.tl_proposed ? Number(form.tl_proposed) : null,
        tenor_months: Number(form.tenor_months) || null,
        interest_rate: Number(form.interest_rate) || null,
        cgtmse_requested: !!form.cgtmse_requested,
        notes: form.notes || null,
        personal_loan_amount: form.personal_loan_amount ? Number(form.personal_loan_amount) : null,
        net_monthly_income: form.net_monthly_income ? Number(form.net_monthly_income) : null,
        monthly_expense: form.monthly_expense ? Number(form.monthly_expense) : null,
        existing_emi: form.existing_emi ? Number(form.existing_emi) : null,
        employer_name: form.employer_name || null,
        employer_type: form.employer_type || null,
        foir_pct: form.foir_pct ? Number(form.foir_pct) : null,
        housing_loan_amount: form.housing_loan_amount ? Number(form.housing_loan_amount) : null,
        property_cost_lakhs: form.property_cost_lakhs ? Number(form.property_cost_lakhs) : null,
        property_location: form.property_location || null,
        rera_number: form.rera_number || null,
        builder_name: form.builder_name || null,
        ltv_pct: form.ltv_pct ? Number(form.ltv_pct) : null,
        property_type: form.property_type || null,
      };
      const res = await api.post("/applications", payload);
      await clearDraft();  // remove the auto-saved draft on success
      toast.success(`Application created: ${res.data.ref_no}`);
      navigate(`/applications/${res.data.id}`);
    } catch (err) {
      let msg = "Failed to create application";
      const detail = err?.response?.data?.detail;
      if (Array.isArray(detail)) {
        msg = detail.map((d) => `${(d.loc || []).slice(-1)[0] || "field"}: ${d.msg}`).join("; ");
      } else if (typeof detail === "string") {
        msg = detail;
      } else if (err?.message) {
        msg = err.message;
      }
      // eslint-disable-next-line no-console
      console.error("Application save error:", err);
      toast.error(msg, { duration: 6000 });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-5 max-w-4xl">
      <SectionHeader title="New Loan Application" subtitle="Walk through the wizard to create a new application." />

      {showBanner && draftMeta?.savedAt && (
        <DraftBanner
          savedAt={draftMeta.savedAt}
          onResume={handleResume}
          onDiscard={handleDiscard}
          label="application draft"
          step={draftMeta.step}
        />
      )}

      {/* Stepper */}
      <div className="flex items-center gap-2 overflow-x-auto pb-2" data-testid="wizard-stepper">
        {STEPS.map((s, i) => (
          <React.Fragment key={s}>
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium border ${i === step ? "bg-accent text-accent-foreground border-accent" : i < step ? "bg-success/10 text-success border-success/30" : "bg-muted/60 text-muted-foreground border-border"}`}>
              <span className="mono">{i < step ? <Check className="h-3 w-3" /> : i + 1}</span>
              <span className="whitespace-nowrap">{s}</span>
            </div>
            {i < STEPS.length - 1 && <div className="h-px flex-1 bg-border" />}
          </React.Fragment>
        ))}
      </div>

      <Card className="p-5 space-y-4">
        {step === 0 && (
          <div className="space-y-3">
            <h3 className="text-sm font-semibold heading">Select Customer</h3>
            <Select value={form.customer_id} onValueChange={(v) => set("customer_id", v)}>
              <SelectTrigger data-testid="wizard-customer-select"><SelectValue placeholder="Choose a customer" /></SelectTrigger>
              <SelectContent>
                {customers.length === 0 && <SelectItem disabled value="none">No customers — onboard one first</SelectItem>}
                {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} ({c.cif})</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">Don't see your customer? <button type="button" onClick={() => navigate("/customers/new")} className="text-accent underline">Onboard a new customer</button></p>
          </div>
        )}
        {step === 1 && (
          <div className="space-y-3">
            <h3 className="text-sm font-semibold heading">Loan Type</h3>
            <Select value={form.loan_type} onValueChange={(v) => set("loan_type", v)}>
              <SelectTrigger data-testid="wizard-loan-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {LOAN_TYPES.map((t) => <SelectItem key={t.code} value={t.code}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>

            <div className="pt-3 border-t border-border space-y-2">
              <Label className="text-xs uppercase tracking-wide font-semibold text-muted-foreground">Application Origin *</Label>
              <div className="grid grid-cols-2 gap-2">
                <button type="button" data-testid="wizard-origin-branch" onClick={() => set("origin", "BRANCH")} className={`rounded-md border p-3 text-left transition-colors ${form.origin === "BRANCH" ? "border-accent bg-accent/8 text-foreground" : "border-border hover:bg-muted/40"}`}>
                  <div className="flex items-center gap-2">
                    <Building2 className="h-4 w-4 text-accent" />
                    <span className="font-medium text-sm">Branch</span>
                  </div>
                  <p className="text-[11px] text-muted-foreground mt-1">App created at the branch. Will be sent to FO queue for document collection + field verification.</p>
                </button>
                <button type="button" data-testid="wizard-origin-fo" onClick={() => set("origin", "FIELD_OFFICE")} className={`rounded-md border p-3 text-left transition-colors ${form.origin === "FIELD_OFFICE" ? "border-accent bg-accent/8 text-foreground" : "border-border hover:bg-muted/40"}`}>
                  <div className="flex items-center gap-2">
                    <UserCheck className="h-4 w-4 text-accent" />
                    <span className="font-medium text-sm">Field Office</span>
                  </div>
                  <p className="text-[11px] text-muted-foreground mt-1">FO has collected docs at customer location. Skips the FO queue step.</p>
                </button>
              </div>
              {form.origin === "BRANCH" && fos.length > 0 && (
                <div className="mt-2 space-y-1">
                  <Label className="text-[11px] uppercase tracking-wide font-semibold text-muted-foreground">Assign Field Officer (optional — auto-assigned if blank)</Label>
                  <Select value={form.assigned_fo_id || "_auto_"} onValueChange={(v) => set("assigned_fo_id", v === "_auto_" ? "" : v)}>
                    <SelectTrigger data-testid="wizard-fo-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_auto_">Auto-assign (first FO of customer's branch)</SelectItem>
                      {fos.map((f) => <SelectItem key={f.id} value={f.id}>{f.name} · {f.branch || "—"}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            <div className="space-y-1.5 pt-3 border-t border-border">
              <Label className="text-xs uppercase tracking-wide font-semibold text-muted-foreground">Purpose</Label>
              <Textarea value={form.purpose} onChange={(e) => set("purpose", e.target.value)} rows={3} placeholder="e.g., Working capital for hardware trading; expansion of shop…" data-testid="wizard-purpose-input" />
            </div>
          </div>
        )}
        {step === 2 && (
          <div className="space-y-3">
            <h3 className="text-sm font-semibold heading">Facility Details (₹ Lakhs)</h3>
            {form.loan_type === "PERSONAL_LOAN" && (
              <div className="space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <FieldRow label="Personal Loan Amount (₹ Lakhs)"><Input type="number" value={form.personal_loan_amount} onChange={(e) => set("personal_loan_amount", e.target.value)} data-testid="wizard-pl-amount" /></FieldRow>
                  <FieldRow label="Net Monthly Income (₹)"><Input type="number" value={form.net_monthly_income} onChange={(e) => set("net_monthly_income", e.target.value)} placeholder="e.g., 80000" data-testid="wizard-pl-income" /></FieldRow>
                  <FieldRow label="Monthly Expense (₹)"><Input type="number" value={form.monthly_expense} onChange={(e) => set("monthly_expense", e.target.value)} placeholder="household burn (excl. EMIs)" data-testid="wizard-pl-expense" /></FieldRow>
                  <FieldRow label="Existing EMI (₹/month)"><Input type="number" value={form.existing_emi} onChange={(e) => set("existing_emi", e.target.value)} data-testid="wizard-pl-existing-emi" /></FieldRow>
                  <FieldRow label="FOIR (%) — auto if blank"><Input type="number" step="0.1" value={form.foir_pct} onChange={(e) => set("foir_pct", e.target.value)} placeholder="≤ 50% policy" /></FieldRow>
                  <FieldRow label="Employer Name"><Input value={form.employer_name} onChange={(e) => set("employer_name", e.target.value)} /></FieldRow>
                  <FieldRow label="Employer Type">
                    <Select value={form.employer_type} onValueChange={(v) => set("employer_type", v)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{["Government", "PSU", "MNC", "Private", "Self-Employed"].map(x => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                    </Select>
                  </FieldRow>
                  <FieldRow label="Tenor (months)"><Input type="number" value={form.tenor_months} onChange={(e) => set("tenor_months", e.target.value)} /></FieldRow>
                  <FieldRow label="Interest Rate (% p.a.)"><Input type="number" step="0.05" value={form.interest_rate} onChange={(e) => set("interest_rate", e.target.value)} /></FieldRow>
                </div>
                <EligibilityPreview
                  inputs={{
                    monthly_income: form.net_monthly_income,
                    monthly_expense: form.monthly_expense,
                    existing_emi: form.existing_emi,
                    tenor_months: form.tenor_months,
                    interest_rate_pa: form.interest_rate,
                    proposed_amount_lakhs: form.personal_loan_amount,
                    max_foir_pct: 50,
                  }}
                  dataTestid="wizard-eligibility-preview"
                />
              </div>
            )}
            {form.loan_type === "HOUSING_LOAN" && (
              <div className="space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <FieldRow label="Housing Loan Amount (₹ Lakhs)"><Input type="number" value={form.housing_loan_amount} onChange={(e) => set("housing_loan_amount", e.target.value)} data-testid="wizard-hl-amount" /></FieldRow>
                  <FieldRow label="Property Cost (₹ Lakhs)"><Input type="number" value={form.property_cost_lakhs} onChange={(e) => set("property_cost_lakhs", e.target.value)} /></FieldRow>
                  <FieldRow label="LTV %"><Input type="number" step="0.1" value={form.ltv_pct} onChange={(e) => set("ltv_pct", e.target.value)} placeholder="≤90% (small) / ≤80% (>30L)" /></FieldRow>
                  <FieldRow label="Property Type">
                    <Select value={form.property_type} onValueChange={(v) => set("property_type", v)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{["New", "Resale", "Construction"].map(x => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                    </Select>
                  </FieldRow>
                  <FieldRow label="Property Location"><Input value={form.property_location} onChange={(e) => set("property_location", e.target.value)} /></FieldRow>
                  <FieldRow label="Builder Name"><Input value={form.builder_name} onChange={(e) => set("builder_name", e.target.value)} /></FieldRow>
                  <FieldRow label="RERA No."><Input value={form.rera_number} onChange={(e) => set("rera_number", e.target.value)} /></FieldRow>
                  <FieldRow label="Net Monthly Income (₹)"><Input type="number" value={form.net_monthly_income} onChange={(e) => set("net_monthly_income", e.target.value)} placeholder="e.g., 120000" data-testid="wizard-hl-income" /></FieldRow>
                  <FieldRow label="Monthly Expense (₹)"><Input type="number" value={form.monthly_expense} onChange={(e) => set("monthly_expense", e.target.value)} placeholder="household burn (excl. EMIs)" data-testid="wizard-hl-expense" /></FieldRow>
                  <FieldRow label="Existing EMI (₹/month)"><Input type="number" value={form.existing_emi} onChange={(e) => set("existing_emi", e.target.value)} data-testid="wizard-hl-existing-emi" /></FieldRow>
                  <FieldRow label="Tenor (months)"><Input type="number" value={form.tenor_months} onChange={(e) => set("tenor_months", e.target.value)} /></FieldRow>
                  <FieldRow label="Interest Rate (% p.a.)"><Input type="number" step="0.05" value={form.interest_rate} onChange={(e) => set("interest_rate", e.target.value)} /></FieldRow>
                </div>
                <EligibilityPreview
                  inputs={{
                    monthly_income: form.net_monthly_income,
                    monthly_expense: form.monthly_expense,
                    existing_emi: form.existing_emi,
                    tenor_months: form.tenor_months,
                    interest_rate_pa: form.interest_rate,
                    proposed_amount_lakhs: form.housing_loan_amount,
                    max_foir_pct: 50,
                  }}
                  dataTestid="wizard-eligibility-preview"
                />
              </div>
            )}
            {form.loan_type !== "PERSONAL_LOAN" && form.loan_type !== "HOUSING_LOAN" && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <FieldRow label="CC Proposed (₹ Lakhs)"><Input type="number" value={form.cc_proposed} onChange={(e) => set("cc_proposed", e.target.value)} data-testid="wizard-cc-input" /></FieldRow>
                  <FieldRow label="TL Proposed (₹ Lakhs)"><Input type="number" value={form.tl_proposed} onChange={(e) => set("tl_proposed", e.target.value)} data-testid="wizard-tl-input" /></FieldRow>
                  <FieldRow label="Tenor (months)"><Input type="number" value={form.tenor_months} onChange={(e) => set("tenor_months", e.target.value)} /></FieldRow>
                  <FieldRow label="Interest Rate (% p.a.)"><Input type="number" step="0.05" value={form.interest_rate} onChange={(e) => set("interest_rate", e.target.value)} /></FieldRow>
                </div>
                <div className="flex items-center justify-between rounded-md border p-3">
                  <div>
                    <div className="text-sm font-medium">Request CGTMSE Coverage</div>
                    <div className="text-xs text-muted-foreground">Hybrid model available; concessional fee for NE region</div>
                  </div>
                  <Switch checked={form.cgtmse_requested} onCheckedChange={(v) => set("cgtmse_requested", v)} data-testid="wizard-cgtmse-switch" />
                </div>
              </>
            )}
          </div>
        )}
        {step === 3 && (
          <div className="space-y-2">
            <h3 className="text-sm font-semibold heading">Notes</h3>
            <Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={6} placeholder="Any internal notes for record…" />
          </div>
        )}
        {step === 4 && (
          <div className="space-y-3">
            <h3 className="text-sm font-semibold heading">Review</h3>
            <ReviewRow k="Customer" v={(customers.find((c) => c.id === form.customer_id)?.name) || "—"} />
            <ReviewRow k="Loan Type" v={form.loan_type} />
            <ReviewRow k="Purpose" v={form.purpose || "—"} />
            <ReviewRow k="CC Proposed" v={`₹ ${form.cc_proposed || 0} L`} />
            <ReviewRow k="TL Proposed" v={`₹ ${form.tl_proposed || 0} L`} />
            <ReviewRow k="Tenor" v={`${form.tenor_months || 0} months @ ${form.interest_rate}%`} />
            <ReviewRow k="CGTMSE" v={form.cgtmse_requested ? "Yes" : "No"} />
          </div>
        )}

        <div className="flex justify-between pt-3 border-t border-border flex-wrap gap-2">
          <Button variant="ghost" onClick={prev} disabled={step === 0}>Back</Button>
          <div className="flex gap-2 flex-wrap">
            <Button
              variant="secondary"
              type="button"
              onClick={handleSaveDraftAndExit}
              disabled={savingDraft || busy}
              data-testid="application-save-draft-button"
            >
              {savingDraft ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
              Save Draft & Exit
            </Button>
            {step < STEPS.length - 1 ? (
              <Button onClick={next} data-testid="wizard-next-button">Next</Button>
            ) : (
              <Button onClick={submit} disabled={busy} data-testid="wizard-submit-button">{busy ? "Submitting…" : "Submit Application"}</Button>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
}

function FieldRow({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs uppercase tracking-wide font-semibold text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}

function ReviewRow({ k, v }) {
  return (
    <div className="flex justify-between text-sm">
      <span className="text-muted-foreground">{k}</span>
      <span className="font-medium mono">{v}</span>
    </div>
  );
}
